import { useMutation } from '@tanstack/react-query';
import {
  BenefitDTOBenefitEnum,
  BulkActivationResultItemDTO,
  BulkActivationUpdateResponseDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useTenantID } from 'probonio-shared-ui/module/me';
import { SetStateAction } from 'react';
import { chunkArray } from '../../../util/chunkArray';
import { extractBulkErrorResults } from '../bulkActivation/extractBulkErrorResults';
import { useRefetchActivations } from '../query';

const BATCH_SIZE = 20;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useBulkUpdateActivationsMutation(
  employeeIds: string[],
  setProgress: (value: SetStateAction<number | undefined>) => void,
  benefit: BenefitDTOBenefitEnum,
  customBenefitId?: string,
) {
  const getTenantId = useTenantID();
  const withMessage = useWithMessage();
  const refetchActivations = useRefetchActivations();

  return useMutation({
    mutationFn: async (bulkUpdate: UpdateBenefitActivationDTO): Promise<BulkActivationUpdateResponseDTO> => {
      const aggregated: BulkActivationResultItemDTO[] = [];

      for (const chunk of chunkArray(employeeIds, BATCH_SIZE)) {
        try {
          const { data } = await apis.benefits.updateBenefitActivations({
            tenantId: getTenantId(),
            bulkUpdateBenefitActivationsDTO: { benefit, customBenefitId, employeeIds: chunk, update: bulkUpdate },
          });
          aggregated.push(...data.results);
        } catch (err) {
          const errorResults = extractBulkErrorResults(err);
          if (errorResults) {
            aggregated.push(...errorResults);
          } else {
            throw err;
          }
        }
        setProgress((aggregated.length / employeeIds.length) * 100);
      }

      return { results: aggregated };
    },

    onError: err => {
      withMessage(err as Error);
    },
    onSettled: () => {
      setProgress(undefined);
    },
    onSuccess: async () => {
      // refetchActivations() already refetches employees internally
      await refetchActivations();
    },
  });
}
